/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import axiosCall from '../services/axios';
import Styles from '../css/DocumentPage.module.css';
import EnvelopeWithBox from '../components/EnvelopeWithBox';
import adobeReaderImage from '../assets/get_adobe_reader.png';
import LoadingModal from '../components/modals/LoadingModal';
import useAbortController from '../common/hooks/useAbortController';
import { useLoanContext } from '../contexts/LoanContext';
import { useAuthContext } from '../contexts/AuthContext';
import useBorrowerData from '../common/hooks/useBorrowerData';
import { useViewingContext } from '../contexts/ViewingContext';
import { shouldShowDocumentsGoGreenSignup } from '../contexts/GoGreenContext';
import { getTimestampAsDate, isStrEmptyOrNull } from '../common/UtilityFunctions';
import DefaultModal from '../components/modals/DefaultModal';
import { Button, IconButton, Tooltip } from '@mui/material';
import { FileDownload, Preview } from '@mui/icons-material';
import ModalStyles from '../css/GoGreenPage.module.css';

type ViewType = 'allDocuments' | 'monthlyStatements';

const VIEW_TABS: { value: ViewType; label: string }[] = [
  { value: 'allDocuments', label: 'All Documents' },
  { value: 'monthlyStatements', label: 'Monthly Statements' },
];

interface LoanData {
  loanNumber: string;
  isLoading: boolean;
  description: string;
  closingDate: string | null;
}

interface LoanDocument {
  documentId: number;
  active: boolean;
  borrowerAccess: boolean;
  dateCreated: number;
  description: string;
  documentType?: string;
  accessLevel?: number;
}

// Judith Espinal - SQC-766
function DocumentsPage() {
  const { loanNumber, primaryBorrowerNumber } = useLoanContext();
  const { token } = useAuthContext();
  const { reset } = useAbortController();
  const { isServicerView } = useViewingContext();
  const { currentBorrower, loadingBorrowerData, reloadBorrowerData, borrowerData } = useBorrowerData();

  const goGreenStatusBorrower = useMemo(() => {
    if (isServicerView) {
      if (primaryBorrowerNumber && borrowerData[primaryBorrowerNumber]) {
        return borrowerData[primaryBorrowerNumber];
      }
      const borrowers = Object.values(borrowerData);
      return borrowers.length > 0 ? borrowers[0] : null;
    }
    return currentBorrower;
  }, [isServicerView, primaryBorrowerNumber, borrowerData, currentBorrower]);

  const showGoGreenSignupPrompt = useMemo(
    () => !loadingBorrowerData && shouldShowDocumentsGoGreenSignup(goGreenStatusBorrower),
    [goGreenStatusBorrower, loadingBorrowerData],
  );

  useEffect(() => {
    reloadBorrowerData();
  }, [reloadBorrowerData]);

  const [loanData, setLoanData] = useState<LoanData>({
    loanNumber: '',
    isLoading: true,
    description: '',
    closingDate: '',
  });

  const [documents, setDocuments] = useState<LoanDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState<ViewType>('allDocuments');
  const [errorDocId, setErrorDocId] = useState<number>(null);

  const closingDate = useMemo(
    () => (loanData.closingDate ? new Date(loanData.closingDate) : null),
    [loanData.closingDate],
  );

  const getLoanData = useCallback(async () => {
    try {
      if (!token || !loanNumber) return;

      const request = { url: `live/loan/${loanNumber}`, method: 'get' as const };
      const config = reset();
      const headers = { 'Session-Token': token };
      const response = await axiosCall(request, config, headers);

      if (!response) throw new Error('Invalid response');

      setLoanData((prev) => ({
        ...prev,
        ...response,
        isLoading: false,
      }));
    } catch (error: any) {
      console.error(`DocumentsPage.getLoanData`, error.message);
    }
  }, [loanNumber, token, reset]);

  useEffect(() => {
    getLoanData();
  }, [getLoanData]);

  const getDocuments = useCallback(async () => {
    if (!token || !loanNumber || !closingDate) return;

    try {
      const request = {
        url: `live/loan/${loanNumber}/documents`,
        method: 'get' as const,
        params: {
          fields: 'documentId,active,borrowerAccess,dateCreated,description,documentType,accessLevel',
        },
      };
      const config = reset();
      const headers = {
        'Session-Token': token,
      };

      const response: LoanDocument[] = await axiosCall(request, config, headers);
      if (!response) throw new Error(`Invalid response for loan number ${loanNumber}`);

      const validDocuments = response.filter((doc) => {
        const docDate = new Date(doc.dateCreated * 1000);

        const hasValidAccessLevel = doc.accessLevel === 2;

        return doc.active === true && doc.borrowerAccess === true && hasValidAccessLevel && docDate >= closingDate;
      });

      setDocuments(validDocuments);
    } catch (error: any) {
      console.error(`Error fetching documents: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [loanNumber, reset, closingDate, token]);

  useEffect(() => {
    getDocuments();
  }, [getDocuments]);

  const visibleDocuments = useMemo(() => {
    if (viewType === 'monthlyStatements') {
      return documents.filter((doc) => doc.documentType === 'MONTHLY_STATEMENTS' || doc.documentType === 'MONSTMT');
    }
    return documents;
  }, [documents, viewType]);

  const viewPdf = useCallback(
    async (documentId: number) => {
      try {
        if (!token || !documentId) return;
        setLoading(true);
        const request = {
          url: `image/${documentId}`,
          method: 'get' as const,
          responseType: 'blob' as const,
        };
        const headers = { 'Session-Token': token };
        const response = await axiosCall(request, undefined, headers);
        setErrorDocId(null);
        const pdfBlob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        window.open(url);
      } catch (error: any) {
        setErrorDocId(documentId);
        console.error('Error viewing PDF:', error.message);
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  const downloadPdf = useCallback(
    async (documentId: number) => {
      try {
        if (!token || !documentId) return;
        setLoading(true);
        const request = {
          url: `image/${documentId}`,
          method: 'get' as const,
          responseType: 'blob' as const,
        };
        const headers = { 'Session-Token': token };
        const response = await axiosCall(request, undefined, headers);
        setErrorDocId(null);
        const pdfBlob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = url.split('/').pop() + '.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error: any) {
        setErrorDocId(documentId);
        console.error('Error downloading PDF:', error.message);
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  const renderDocumentActions = useCallback(
    (documentId: number) => (
      <>
        <Tooltip title='View as PDF'>
          <IconButton
            className={Styles.documentLink}
            onClick={(e) => {
              e.preventDefault();
              viewPdf(documentId);
            }}
          >
            <Preview className={Styles.documentLink} />
          </IconButton>
        </Tooltip>
        <Tooltip title='Download PDF'>
          <IconButton
            className={Styles.documentLink}
            onClick={(e) => {
              e.preventDefault();
              downloadPdf(documentId);
            }}
          >
            <FileDownload className={Styles.documentLink} />
          </IconButton>
        </Tooltip>
      </>
    ),
    [downloadPdf, viewPdf],
  );

  const errorModalButtons = () => {
    return (
      <>
        <Button
          fullWidth
          variant='contained'
          onClick={() => {
            setErrorDocId(null);
            viewPdf(errorDocId);
          }}
          className={ModalStyles.primbutton}
        >
          {'Try Again'}
        </Button>

        <Button
          fullWidth
          variant='contained'
          onClick={() => {
            setErrorDocId(null);
          }}
          className={ModalStyles.secondarybutton}
        >
          {'Cancel'}
        </Button>
      </>
    );
  };

  return (
    <div className={Styles.myComponent}>
      <EnvelopeWithBox blockName='Documents' className={`${Styles.documentsEnvelope} ${Styles.documentsAccent}`}>
        <div className={`${Styles.goGreenContainer} ${Styles.goGreenContainerNoPromo}`}>
          <div className={Styles.viewSwitcher}>
            <select
              id='viewSelector'
              aria-label='Filter documents'
              value={viewType}
              onChange={(e) => setViewType(e.target.value as ViewType)}
            >
              <option value='allDocuments'>All Documents</option>
              <option value='monthlyStatements'>Monthly Statements</option>
            </select>
          </div>
        </div>
        <div className={Styles.documentFilter}>
          <div className={Styles.documentFilterSegments} role='group' aria-label='Filter documents'>
            {VIEW_TABS.map((tab) => (
              <button
                key={tab.value}
                type='button'
                className={`${Styles.documentFilterOption} ${
                  viewType === tab.value ? Styles.documentFilterOptionActive : ''
                }`}
                aria-pressed={viewType === tab.value}
                onClick={() => setViewType(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className={Styles.tableContainer}>
          <table className={Styles.documentTable}>
            <thead>
              <tr className={Styles.stickyHeader}>
                <th>Date</th>
                <th>Document Name</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              {visibleDocuments.length === 0 ? (
                <tr>
                  <td colSpan={3} className={Styles.noStatementsText}>
                    No Documents
                  </td>
                </tr>
              ) : (
                visibleDocuments.map((doc) => (
                  <tr key={doc.documentId}>
                    <td className={Styles.mainTextColor}>{getTimestampAsDate(doc.dateCreated)}</td>
                    <td className={Styles.mainTextColor}>{doc.description}</td>
                    <td className={Styles.mainTextColor}>{renderDocumentActions(doc.documentId)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!isStrEmptyOrNull(errorDocId?.toString()) && (
          <DefaultModal
            open={true}
            className={`${ModalStyles.modalcontainer} ${Styles.documentErrorModal}`}
            title={<div className={ModalStyles.modaltitle}>Error</div>}
            content={
              <p className={`${ModalStyles.modalcontent} ${Styles.documentErrorContent}`}>
                There was an error downloading the document.
              </p>
            }
            footer={
              <div className={`${ModalStyles.buttonrow} ${Styles.documentErrorButtonRow}`}>{errorModalButtons()}</div>
            }
          ></DefaultModal>
        )}
        {(loanData.isLoading || loading) && <LoadingModal open={true} />}
      </EnvelopeWithBox>

      {showGoGreenSignupPrompt && (
        <div className={Styles.goGreenBelowTable}>
          <Button component={NavLink} to='/go-green' variant='contained' className={Styles.goGreenPromoButton}>
            Go Green
          </Button>
        </div>
      )}

      <div className={Styles.adobeSection}>
        <p>If you are unable to open your document from the above links, you may need to download a PDF viewer.</p>
        <p>Please click the image below to download the free Adobe Reader application.</p>
        <a href='https://get.adobe.com/reader/'>
          <img src={adobeReaderImage} alt='Get Adobe Reader' />
        </a>
      </div>
    </div>
  );
}

export default DocumentsPage;
